import React, { useState, useRef, useEffect } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdOutlineDashboard,
  MdPeople,
  MdEvent,
  MdSettings,
} from "react-icons/md";
import {
  IoPersonOutline,
  IoReceiptOutline,
} from "react-icons/io5";
import {
  LuCalendarDays,
  LuCalendarMinus,
  LuClipboardPlus,
} from "react-icons/lu";
import {
  TbCalendarTime,
  TbClockPlus,
  TbUsersGroup,
  TbReportAnalytics,
} from "react-icons/tb";
import { HiOutlineClipboardList } from "react-icons/hi";
import { GrUserWorker } from "react-icons/gr";
import { Megaphone, TentTree } from "lucide-react";
import { useUser } from "../../context/UserContext";


function SearchBar({ onNavigate }) {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const suggestionRef = useRef();

  // Comprehensive route definitions
  const allRoutes = [
    // Employee Routes
    {
      name: "Dashboard",
      path: "/employee/dashboard",
      icon: MdOutlineDashboard,
      keywords: ["home", "overview", "main"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "Pay Slips",
      path: "/employee/payslips",
      icon: IoReceiptOutline,
      keywords: ["salary", "payment", "earnings", "payslip"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "Attendance",
      path: "/employee/attendance",
      icon: LuCalendarDays,
      keywords: ["attendance", "presence", "check-in"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "Apply Leaves",
      path: "/employee/leaves",
      icon: LuCalendarMinus,
      keywords: ["leave", "vacation", "time off", "absence"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "On Duty",
      path: "/employee/on-duty",
      icon: GrUserWorker,
      keywords: ["od", "duty", "work"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "Comp Off",
      path: "/employee/comp-off",
      icon: TbClockPlus,
      keywords: ["compensation", "overtime", "extra hours"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "Announcements",
      path: "/employee/events/annoucements",
      icon: Megaphone,
      keywords: ["news", "updates", "notice"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "Holidays",
      path: "/employee/events/holidays",
      icon: TentTree,
      keywords: ["holiday", "festival", "off days"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    {
      name: "Profile",
      path: "/employee/profile",
      icon: IoPersonOutline,
      keywords: ["account", "settings", "personal"],
      roles: ["EMPLOYEE", "MANAGER"],
    },
    
    // Manager-specific Routes
    {
      name: "Leave Requests",
      path: "/employee/leave-requests",
      icon: HiOutlineClipboardList,
      keywords: ["approve", "pending", "team leaves"],
      roles: ["MANAGER"],
    },
    {
      name: "Comp Off Requests",
      path: "/employee/comp-off-requests",
      icon: LuClipboardPlus,
      keywords: ["approve comp", "team comp off"],
      roles: ["MANAGER"],
    },
    {
      name: "On Duty Requests",
      path: "/employee/on-duty-requests",
      icon: TbReportAnalytics,
      keywords: ["approve od", "team on duty"],
      roles: ["MANAGER"],
    },

    // Admin Routes
    {
      name: "Admin Dashboard",
      path: "/admin/dashboard",
      icon: MdOutlineDashboard,
      keywords: ["admin home", "overview"],
      roles: ["ADMIN"],
    },
    {
      name: "Employees",
      path: "/admin/employees",
      icon: MdPeople,
      keywords: ["staff", "team", "workforce"],
      roles: ["ADMIN"],
    },
    {
      name: "Events",
      path: "/admin/events",
      icon: MdEvent,
      keywords: ["calendar", "schedule"],
      roles: ["ADMIN"],
    },
    {
      name: "Settings",
      path: "/admin/settings",
      icon: MdSettings,
      keywords: ["configuration", "preferences"],
      roles: ["ADMIN"],
    },

    // HR Routes
    {
      name: "HR Dashboard",
      path: "/hr/dashboard",
      icon: MdOutlineDashboard,
      keywords: ["hr home", "hr overview"],
      roles: ["HR"],
    },
    {
      name: "Active Employees",
      path: "/hr/employees/active",
      icon: TbUsersGroup,
      keywords: ["staff", "active workers"],
      roles: ["HR"],
    },
    {
      name: "Inactive Employees",
      path: "/hr/employees/in-active",
      icon: TbUsersGroup,
      keywords: ["inactive staff", "former employees"],
      roles: ["HR"],
    },
    {
      name: "Departments",
      path: "/hr/departments",
      icon: TbUsersGroup,
      keywords: ["teams", "divisions"],
      roles: ["HR"],
    },
    {
      name: "Daily Attendance",
      path: "/hr/daily-attendance",
      icon: LuCalendarDays,
      keywords: ["today attendance", "daily presence"],
      roles: ["HR"],
    },
    {
      name: "Monthly Attendance",
      path: "/hr/monthly-attendance",
      icon: LuCalendarDays,
      keywords: ["month attendance", "attendance report"],
      roles: ["HR"],
    },
    {
      name: "Leave Reports",
      path: "/hr/leave-reports",
      icon: TbReportAnalytics,
      keywords: ["leave analytics", "absence report"],
      roles: ["HR"],
    },
    {
      name: "Comp Off Reports",
      path: "/hr/compOff-reports",
      icon: TbReportAnalytics,
      keywords: ["comp analytics"],
      roles: ["HR"],
    },
    {
      name: "On Duty Reports",
      path: "/hr/onduty-reports",
      icon: TbReportAnalytics,
      keywords: ["od analytics"],
      roles: ["HR"],
    },
    {
      name: "Add Employee",
      path: "/hr/AddEmployee",
      icon: MdPeople,
      keywords: ["new employee", "recruit", "hire"],
      roles: ["HR"],
    },
    {
      name: "Leave Balance Management",
      path: "/hr/employees/leave-balance-management",
      icon: LuCalendarMinus,
      keywords: ["leave balance", "quota"],
      roles: ["HR"],
    },
  ];

  // Filter routes based on user role and search term
  const filterRoutes = (term) => {
    if (!term.trim()) return [];

    const lowerTerm = term.toLowerCase();
    
    return allRoutes
      .filter((route) => {
        // Check if user has access to this route
        if (!route.roles.includes(currentUser?.role)) return false;

        // Search in name and keywords
        const nameMatch = route.name.toLowerCase().includes(lowerTerm);
        const keywordMatch = route.keywords.some((keyword) =>
          keyword.toLowerCase().includes(lowerTerm)
        );

        return nameMatch || keywordMatch;
      })
      .slice(0, 8); // Limit to 8 suggestions
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSuggestions(filterRoutes(value));
  };

  const handleSuggestionClick = (route) => {
    navigate(route.path);
    setSearchTerm("");
    setSuggestions([]);
    setIsSearchFocused(false);
    
    // Call the onNavigate callback if provided (for mobile)
    if (onNavigate) {
      onNavigate();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      handleSuggestionClick(suggestions[0]);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setSuggestions([]);
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={suggestionRef}>
      <form
        onSubmit={handleSubmit}
        className="w-full sm:w-72 bg-white border border-slate-200 p-2 rounded-full flex items-center shadow-md hover:shadow-lg transition-shadow"
      >
        <FaSearch className="text-slate-400 ml-2 flex-shrink-0" />
        <input
          type="text"
          placeholder="Search pages..."
          className="bg-transparent focus:outline-none px-3 py-1.5 w-full text-sm text-slate-700 placeholder-slate-400"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => {
            setIsSearchFocused(true);
            if (searchTerm) setSuggestions(filterRoutes(searchTerm));
          }}
          autoFocus
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm("");
              setSuggestions([]);
            }}
            className="text-slate-400 hover:text-slate-600 mr-1 text-xl"
          >
            ×
          </button>
        )}
      </form>

      {/* Suggestions Dropdown */}
      <AnimatePresence>
        {suggestions.length > 0 && isSearchFocused && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full mt-2 left-0 w-full sm:w-96 bg-white border border-slate-200 shadow-xl rounded-lg z-50 overflow-hidden max-h-96 overflow-y-auto"
          >
            <div className="py-2">
              {suggestions.map((route, idx) => {
                const Icon = route.icon;
                return (
                  <motion.button
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => handleSuggestionClick(route)}
                    className="w-full px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-slate-100 group-hover:bg-orange-100 transition-colors">
                      <Icon className="text-slate-600 group-hover:text-orange-600 transition-colors" size={18} />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-medium text-slate-700 group-hover:text-orange-600 transition-colors">
                        {route.name}
                      </span>
                      <span className="text-xs text-slate-400">
                        {route.path}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
            <div className="border-t border-slate-100 px-4 py-2 bg-slate-50">
              <p className="text-xs text-slate-400 text-center">
                Press Enter to go to first result
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SearchBar;